import {
  Banner,
  Button,
  FieldLabel,
  FieldSwitch,
  FieldText,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  IconButton,
  Page,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import {
  addSubscription,
  getEvents,
  getSubscription,
  updateSubscription,
} from 'components/settings/api/webhooks';
import { validateName } from 'utils/formValidator';

import { webhooksBasePath } from '../../webhooks.utils';
import { AuthorizationHeader } from '../authorizationHeader';

import { EventPicker } from './components/eventPicker';
import styles from './subscriptionForm.module.scss';
import {
  toCreatePayload,
  toFormValue,
  toUpdatePayload,
  validate,
} from './subscriptionForm.utils';

type SubscriptionFormOwnProps = {
  mode: 'create' | 'edit';
};

export type FormValueProps = {
  id?: string;
  webhookName: string;
  webhookUrl: string;
  headers: Record<string, string>;
  subscriptionEvents: string[];
  isEnabled: boolean;
};

export type SubscriptionFormProps = SubscriptionFormOwnProps &
  InjectedFormProps<FormValueProps, SubscriptionFormOwnProps, FormErrors>;

function SubscriptionForm({
  mode,
  initialize,
  handleSubmit,
}: SubscriptionFormProps): JSX.Element {
  const isEditMode = mode === 'edit';

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [error, setError] = useState<string | null>();

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  useEffect(() => {
    setError(null);

    getEvents()
      .then(({ data }) => setEvents(data.fqns))
      .catch(() =>
        setError('Something went wrong while fetching the webhook events.'),
      );

    if (id) {
      getSubscription(id)
        .then(({ data }) => initialize(toFormValue(data)))
        .catch(() =>
          setError('Something went wrong while fetching the webhook.'),
        );
    } else if (isEditMode) {
      setError('The id to fetch the subscription is missing.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = useCallback(
    (values) => {
      setError(null);

      if (isEditMode) {
        setIsUpdating(true);

        updateSubscription(toUpdatePayload(values))
          .then((response) => {
            if ('errors' in response) {
              setError(response.errors[0]);
            }

            if ('data' in response) {
              const { data } = response;

              ampli.settingsIntegrationsWebhooksEditSave();
              navigate(`${webhooksBasePath}/${data.id}`);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setIsUpdating(false);
            setError(
              `Something went wrong while updating the webhook. ${error}`,
            );
          });
      } else {
        setIsCreating(true);
        addSubscription(toCreatePayload(values))
          .then((response) => {
            if ('errors' in response) {
              setError(response.errors[0]);
            }

            if ('data' in response) {
              const { data } = response;

              ampli.settingsIntegrationsWebhooksAddConfirm();
              navigate(`${webhooksBasePath}/${data.id}`);
            }
            setIsCreating(false);
          })
          .catch((error) => {
            setIsCreating(false);
            setError(
              `Something went wrong while creating the webhook. ${error}`,
            );
          });
      }
    },
    [navigate, isEditMode],
  );

  return (
    <Page.Section
      title={`${isEditMode ? 'Edit' : 'Create'} webhook`}
      titleMeta={
        <Tooltip content="Help and documentation.">
          <IconButton
            icon="question"
            variant="tertiary"
            onClick={() => {
              ampli.settingsIntegrationsWebhooksDocumentationLinkClicked();

              window.open(
                'https://docs.dixa.io/docs/webhooks-guide',
                '_blank',
                'noopener,noreferrer',
              );
            }}
          />
        </Tooltip>
      }
      controls={
        <Button
          isLoading={isCreating || isUpdating}
          disabled={isEditMode ? !id : false}
          onClick={handleSubmit(handleFormSubmit)}
        >
          Save
        </Button>
      }
    >
      {error ? (
        <Text.Paragraph>{error}</Text.Paragraph>
      ) : (
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <FlexBox flexDirection="row" margin="300 0 0 0">
            <div className={styles.details}>
              <FormGrid>
                <FormField>
                  <Field
                    label="Name"
                    name="webhookName"
                    component={FieldText}
                    type="text"
                    placeholder="Name"
                    helpIcon={{
                      text: 'Type a name here, so you can recognise your webhook easily later.',
                    }}
                    validate={validateName}
                  />
                </FormField>
                <FormField>
                  <Field
                    label="Webhook URL"
                    name="webhookUrl"
                    component={FieldText}
                    type="text"
                    placeholder="Webhook URL"
                    helpIcon={{
                      text: 'Specify the URL that the events should be sent to. This could be an API from your internal system or e.g. a CRM like Salesforce.',
                    }}
                  />
                </FormField>
                <FormField>
                  <FieldWrapper label="Enable">
                    <Field
                      name="isEnabled"
                      component={({ input }) => (
                        <FieldSwitch
                          input={{
                            ...input,
                            checked: input.value,
                          }}
                        />
                      )}
                    />
                  </FieldWrapper>
                </FormField>
                <FormField>
                  <Field name="headers" component={AuthorizationHeader} />
                </FormField>
              </FormGrid>
            </div>
            <div className={styles.events}>
              <FormGrid>
                <FormField>
                  <FieldLabel
                    className={styles.eventLabel}
                    helpIcon={{
                      text: 'Select the events you are interested in getting delivered to your systems.',
                    }}
                  >
                    Events
                  </FieldLabel>
                  <Field
                    name="subscriptionEvents"
                    events={events}
                    component={({ input, meta, events }) => (
                      <EventPicker
                        events={events || []}
                        input={input}
                        meta={meta}
                      />
                    )}
                  />
                </FormField>
              </FormGrid>
            </div>
          </FlexBox>
        </Form>
      )}

      {!isEditMode && !error && (
        <FlexBox margin="200 0 0 0">
          <Banner
            variant="info"
            description={
              <>
                By creating this webhook subscription, you instruct Dixa to
                transfer data to the organization owning the domain URL in the
                form below (input field &apos;Webhook URL&apos;). Please note
                this may include personal data.
              </>
            }
          />
        </FlexBox>
      )}
    </Page.Section>
  );
}

const SubscriptionFormRedux = reduxForm<
  FormValueProps,
  SubscriptionFormOwnProps,
  FormErrors
>({
  form: 'webhooksSubscriptionForm',
  validate,
})(SubscriptionForm);

export { SubscriptionFormRedux as SubscriptionForm };
